import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import {
  createTenant,
  fetchMyTenants,
  setActiveTenantId,
} from '../api.js';
import { useToast } from './Toast.jsx';
import { Wizard, WizardStep } from './Wizard.jsx';

/**
 * Tenant breadcrumb selector (card 050).
 *
 * Rendered inside HeaderBar.  Displays the active tenant's name as
 * a Clerk-dashboard-style breadcrumb chip; click opens a popover
 * with a searchable list of the platform user's memberships.
 *
 * Three states this component handles end-to-end:
 *
 *   - LOADING   — initial fetch of /v1/platform/tenants in flight.
 *                 Render a skeleton chip so the bar doesn't pop
 *                 layout when results land.
 *   - EMPTY     — user has zero memberships.  Open state surfaces a
 *                 "no tenants yet" empty card.  Super-admins see a
 *                 "Create new tenant" affordance there; everyone
 *                 else sees a "ask to be invited" hint.
 *                 We NEVER render a 4xx "tenant not found" red
 *                 banner — the empty state IS the experience.
 *   - LOADED    — chip shows the active tenant's name (or "Choose
 *                 tenant" if nothing is selected); popover lists
 *                 each tenant with a search box once there are
 *                 more than ~5.  Search matches name + slug.
 *
 * Security:
 *
 *   The list rendered here is exactly what `/v1/platform/tenants`
 *   returned — the server filters by the caller's memberships.  We
 *   never enumerate tenants client-side or expose IDs we didn't
 *   receive from that endpoint.
 *
 * Create flow:
 *
 *   Slug input first, display-name pre-filled from it (slug is what
 *   the user types; the name is just a friendlier rendering until
 *   they override it).  Card body specifically calls this out as a
 *   regression in the v1 IA where the name was empty by default and
 *   tenant pages later showed "(unknown)".
 */
export function TenantPicker({
  tenants,
  isSuperAdmin,
  loading,
  activeTenantId,
  onReload,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);

  // Outside-click + Escape close, scoped to the popover root so a
  // click on the page content (eg a sidebar link) still routes.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target)) return;
      setOpen(false);
      setCreating(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (creating) {
          setCreating(false);
        } else {
          setOpen(false);
          triggerRef.current?.focus?.();
        }
      }
    };
    window.addEventListener('click', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, creating]);

  // Autofocus the search box when the popover opens with a non-trivial
  // list so the user can type immediately.  Skip when they've gone
  // into create mode so focus stays on the create form.
  useEffect(() => {
    if (!open || creating) return;
    if (tenants.length > 5) {
      // setTimeout 0 so the input is mounted before we try to focus.
      const t = window.setTimeout(() => searchRef.current?.focus?.(), 0);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open, creating, tenants.length]);

  const activeTenant = useMemo(
    () => tenants.find((t) => t.tenant_id === activeTenantId) || null,
    [tenants, activeTenantId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((t) => {
      const haystack = [t.name, t.slug, t.tenant_id].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [tenants, query]);

  function selectTenant(tenantId) {
    if (tenantId === activeTenantId) {
      setOpen(false);
      return;
    }
    // setActiveTenantId reloads the page after persisting — see api.js
    // for the rationale (resets every in-memory fetch cache so we don't
    // leak the old tenant's data into the new tenant's pages).
    setActiveTenantId(tenantId);
  }

  // Skeleton chip while the tenants fetch is in flight on first paint.
  if (loading && tenants.length === 0 && !activeTenant) {
    return (
      <div className="hb-picker hb-picker--loading">
        <span className="hb-picker__skeleton" aria-hidden="true" />
        <span className="hb-picker__skeleton hb-picker__skeleton--sm" aria-hidden="true" />
        <span className="sr-only">Loading tenants…</span>
      </div>
    );
  }

  const triggerLabel =
    activeTenant?.name
    || (tenants.length === 0 ? 'Choose tenant' : 'Select tenant');

  return (
    <div className="hb-picker" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        data-ui-name="tenant-picker"
        className={`hb-picker__trigger${open ? ' hb-picker__trigger--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <TenantIcon />
        <span className="hb-picker__label">{triggerLabel}</span>
        {activeTenant?.role && (
          <span className="hb-picker__role">{activeTenant.role}</span>
        )}
        <ChevronDownIcon />
      </button>

      {creating && (
        <CreateTenantWizard
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            setOpen(false);
            onReload?.();
          }}
        />
      )}

      {open && (
        <div className="hb-picker__popover" role="menu" aria-label="Tenants">
          {false ? null : (
            <>
              {tenants.length > 5 && (
                <div className="hb-picker__search">
                  <SearchIcon />
                  <input
                    ref={searchRef}
                    type="search"
                    className="hb-picker__search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search tenants…"
                    aria-label="Search tenants"
                  />
                </div>
              )}

              {tenants.length === 0 ? (
                <div className="hb-picker__empty">
                  <strong>No tenants yet.</strong>
                  <p>
                    {isSuperAdmin
                      ? 'Create your first tenant to get started.'
                      : 'Ask an existing admin to invite you, then come back here.'}
                  </p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="hb-picker__empty">
                  No tenants match &ldquo;{query}&rdquo;.
                </div>
              ) : (
                <ul className="hb-picker__list">
                  {filtered.map((t) => (
                    <li key={t.tenant_id}>
                      <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={t.tenant_id === activeTenantId}
                        className={`hb-picker__item${
                          t.tenant_id === activeTenantId ? ' hb-picker__item--active' : ''
                        }`}
                        onClick={() => selectTenant(t.tenant_id)}
                      >
                        <span className="hb-picker__item-main">
                          <span className="hb-picker__item-name">{t.name || t.slug || t.tenant_id}</span>
                          {t.slug && t.slug !== t.name && (
                            <span className="hb-picker__item-slug">{t.slug}</span>
                          )}
                        </span>
                        {t.role && (
                          <span className="hb-picker__item-role">{t.role}</span>
                        )}
                        {t.tenant_id === activeTenantId && (
                          <CheckIcon />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {isSuperAdmin && (
                <>
                  <div className="hb-picker__divider" role="separator" />
                  <button
                    type="button"
                    className="hb-picker__action"
                    onClick={() => {
                      // Close the popover first so the wizard's own
                      // dismiss rules (Esc / Cancel only — no click-
                      // outside) own the dismiss surface for the create
                      // flow.  Without this the popover's window-level
                      // outside-click handler would fire on every
                      // backdrop click and slam Cancel.
                      setOpen(false);
                      setCreating(true);
                    }}
                  >
                    <PlusIcon />
                    <span>Create new tenant</span>
                    <span className="hb-picker__action-tag">super-admin</span>
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}

      <style>{HEADER_PICKER_CSS}</style>
    </div>
  );
}

/**
 * Three-step super-admin "Create tenant" wizard (card 059).
 *
 *   1. Slug + display name — slug-first input (the URL handle is
 *      what the operator actually has to choose carefully) with the
 *      display name pre-filled from the slug.  Per card 050: this
 *      is the inverse of the v1 form, which kept producing tenants
 *      with empty display names because users typed the slug into
 *      "name" and never touched "display name".
 *   2. Region — surfaced so the operator confirms the data
 *      residency before submit.  The current backend pins region
 *      via deploy config; the field is informational today but
 *      lives in the draft so it's wired for when create accepts
 *      a region param.
 *   3. Review + create — confirm + submit.  Setting the active
 *      tenant reloads the dashboard with the new tenant in scope.
 *
 * Security: the popover only renders the launch button when
 * `isSuperAdmin` is true (caller responsibility).  The server-side
 * tenant-create endpoint enforces super-admin auth too.
 */
function CreateTenantWizard({ onCancel, onCreated }) {
  const { getToken } = useAuth();
  const { showToast } = useToast();

  const initial = {
    slug: '',
    name: '',
    nameDirty: false,
    region: 'us-east-1',
  };

  function deriveSlug(v) {
    return v.toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+/, '').slice(0, 64);
  }

  return (
    <Wizard
      id="create-tenant"
      title="Create new tenant"
      subtitle="Super-admin only — provisions a fresh tenant container."
      initialValues={initial}
      submitLabel="Create tenant"
      onCancel={onCancel}
      onSubmit={async (values) => {
        const tenant = await createTenant(getToken, {
          name: values.name.trim(),
          slug: values.slug.trim(),
        });
        showToast({
          title: `Created tenant "${tenant?.name || values.name}"`,
          variant: 'success',
        });
        if (tenant?.tenant_id) {
          // setActiveTenantId triggers a full reload — this kicks the
          // rest of the dashboard into the new tenant scope.
          setActiveTenantId(tenant.tenant_id);
        } else {
          onCreated?.();
        }
      }}
    >
      <WizardStep
        title="Slug + display name"
        isValid={(v) => /^[a-z0-9][a-z0-9_-]*$/.test(v.slug.trim()) && v.name.trim().length > 0}
      >
        {({ values, setMany }) => (
          <div className="wiz-stack">
            <label className="field">
              <span className="field__label">URL slug</span>
              <input
                className="input"
                value={values.slug}
                onChange={(e) => {
                  const cleaned = deriveSlug(e.target.value);
                  setMany({
                    ...values,
                    slug: cleaned,
                    name: values.nameDirty ? values.name : slugToDisplayName(cleaned),
                  });
                }}
                placeholder="acme"
                pattern="[a-z0-9][a-z0-9_-]*"
              />
              <p className="wiz-hint">
                Lower-case URL handle. Tenant ID itself is a UUID minted automatically.
              </p>
            </label>
            <label className="field">
              <span className="field__label">Display name</span>
              <input
                className="input"
                value={values.name}
                onChange={(e) => setMany({ ...values, name: e.target.value, nameDirty: true })}
                placeholder="Acme Inc"
              />
              <p className="wiz-hint">
                Pre-filled from the slug — edit if you want something different.
              </p>
            </label>
          </div>
        )}
      </WizardStep>

      <WizardStep title="Region">
        {({ values, set }) => (
          <div className="wiz-stack">
            <label className="field">
              <span className="field__label">Data residency region</span>
              <select
                className="select"
                value={values.region}
                onChange={(e) => set('region', e.target.value)}
              >
                <option value="us-east-1">US East (N. Virginia)</option>
                <option value="eu-west-2">EU West (London)</option>
              </select>
              <p className="wiz-hint">
                Pinned by deploy config today — surfaced here so you
                know which region the new tenant will land in before
                clicking Create.
              </p>
            </label>
          </div>
        )}
      </WizardStep>

      <WizardStep title="Review">
        {({ values }) => (
          <div className="wiz-stack">
            <p className="wiz-hint" style={{ margin: 0 }}>
              We&apos;ll create the tenant and switch your dashboard
              session into it. You&apos;ll start on its empty Home page;
              create a project from there.
            </p>
            <dl className="wiz-review">
              <dt>Slug</dt>
              <dd><code>{values.slug}</code></dd>
              <dt>Name</dt>
              <dd>{values.name}</dd>
              <dt>Region</dt>
              <dd>{values.region}</dd>
            </dl>
          </div>
        )}
      </WizardStep>
    </Wizard>
  );
}

function slugToDisplayName(slug) {
  if (!slug) return '';
  return slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join(' ');
}

/** Hook: load the platform user's tenant memberships once. */
export function useMyTenants() {
  const { getToken } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchMyTenants(getToken)
      .then(({ tenants: list, isSuperAdmin: sa }) => {
        if (!alive) return;
        setTenants(list);
        setIsSuperAdmin(sa);
      })
      .catch(() => {
        // Non-fatal — the picker shows the empty state.  We swallow
        // the error so the dashboard renders even when /platform is
        // unreachable from the operator's network (eg in dev with
        // the backend down).
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [getToken, reloadCount]);

  function reload() { setReloadCount((c) => c + 1); }

  return { tenants, isSuperAdmin, loading, reload };
}

// ───────── icons + CSS shared with ProjectPicker

function TenantIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M9 8h.01" />
      <path d="M15 8h.01" />
      <path d="M9 12h.01" />
      <path d="M15 12h.01" />
      <path d="M9 16h.01" />
      <path d="M15 16h.01" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

export const HEADER_PICKER_CSS = `
  .hb-picker {
    position: relative;
    display: inline-block;
  }

  .hb-picker__trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 8px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: var(--r-md, 10px);
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    color: var(--text-1, #0f172a);
    transition: background 120ms ease, border-color 120ms ease;
    max-width: 240px;
  }

  .hb-picker__trigger:hover,
  .hb-picker__trigger--open {
    background: var(--surface-1, #f8fafc);
    border-color: var(--border-1, #e2e8f0);
  }

  .hb-picker__trigger:focus-visible {
    outline: 2px solid var(--brand-500, #6366f1);
    outline-offset: 1px;
  }

  .hb-picker__label {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 160px;
  }

  .hb-picker__role {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--brand-700, #4338ca);
    background: var(--brand-50, #eef2ff);
    padding: 1px 6px;
    border-radius: var(--r-pill, 999px);
    flex: 0 0 auto;
  }

  .hb-picker__popover {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    min-width: 280px;
    max-width: 360px;
    background: var(--surface-0, #ffffff);
    border: 1px solid var(--border-1, #e2e8f0);
    border-radius: var(--r-md, 10px);
    box-shadow: var(--shadow-2, 0 4px 14px -4px rgba(15, 23, 42, 0.1));
    padding: 6px;
    z-index: 60;
  }

  .hb-picker__search {
    position: relative;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    color: var(--text-3, #64748b);
  }

  .hb-picker__search-input {
    flex: 1;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 13px;
    color: var(--text-1, #0f172a);
    outline: none;
    padding: 0;
  }

  .hb-picker__search-input::placeholder {
    color: var(--text-3, #64748b);
  }

  .hb-picker__list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 280px;
    overflow-y: auto;
  }

  .hb-picker__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 7px 10px;
    background: transparent;
    border: 0;
    border-radius: var(--r-sm, 6px);
    color: var(--text-1, #0f172a);
    font: inherit;
    font-size: 13px;
    text-align: left;
    cursor: pointer;
    transition: background 100ms ease;
  }

  .hb-picker__item:hover {
    background: var(--surface-1, #f1f5f9);
  }

  .hb-picker__item--active {
    background: var(--brand-50, #eef2ff);
    color: var(--brand-700, #4338ca);
  }

  .hb-picker__item--active:hover {
    background: var(--brand-50, #eef2ff);
  }

  .hb-picker__item-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .hb-picker__item-name {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .hb-picker__item-slug {
    font-family: var(--font-mono, monospace);
    font-size: 11px;
    color: var(--text-3, #64748b);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .hb-picker__item-role {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--brand-700, #4338ca);
    background: var(--brand-50, #eef2ff);
    padding: 1px 6px;
    border-radius: var(--r-pill, 999px);
    flex: 0 0 auto;
  }

  .hb-picker__item--active > .hb-picker__item-role {
    background: var(--brand-100, #e0e7ff);
  }

  .hb-picker__empty {
    padding: 14px;
    color: var(--text-2, #334155);
    font-size: 13px;
    line-height: 1.45;
  }

  .hb-picker__empty strong {
    display: block;
    font-size: 13.5px;
    color: var(--text-1, #0f172a);
    margin-bottom: 4px;
  }

  .hb-picker__empty p {
    margin: 0;
    color: var(--text-3, #64748b);
    font-size: 12.5px;
  }

  .hb-picker__divider {
    height: 1px;
    background: var(--border-1, #e2e8f0);
    margin: 4px 2px;
  }

  .hb-picker__action {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 7px 10px;
    background: transparent;
    border: 0;
    border-radius: var(--r-sm, 6px);
    color: var(--brand-700, #4338ca);
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
    transition: background 100ms ease;
  }

  .hb-picker__action:hover {
    background: var(--brand-50, #eef2ff);
  }

  .hb-picker__action-tag {
    margin-left: auto;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-3, #64748b);
    background: var(--surface-1, #f1f5f9);
    padding: 1px 6px;
    border-radius: var(--r-pill, 999px);
  }

  .hb-picker__create {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;
    min-width: 280px;
  }

  .hb-picker__create-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--text-1, #0f172a);
  }

  .hb-picker__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .hb-picker__field-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-2, #334155);
  }

  .hb-picker__field-input {
    padding: 7px 10px;
    border: 1px solid var(--border-2, #cbd5e1);
    border-radius: var(--r-sm, 6px);
    font: inherit;
    font-size: 13px;
    color: var(--text-1, #0f172a);
    background: var(--surface-0, #ffffff);
    transition: border-color 120ms ease, box-shadow 120ms ease;
  }

  .hb-picker__field-input:focus {
    outline: none;
    border-color: var(--brand-500, #6366f1);
    box-shadow: 0 0 0 3px var(--brand-50, #eef2ff);
  }

  .hb-picker__field-hint {
    font-size: 11.5px;
    color: var(--text-3, #64748b);
    line-height: 1.4;
  }

  .hb-picker__error {
    margin: 0;
    color: var(--danger-700, #b91c1c);
    font-size: 12px;
  }

  .hb-picker__create-actions {
    display: flex;
    gap: 6px;
  }

  .hb-picker--loading {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px;
  }

  .hb-picker__skeleton {
    display: inline-block;
    width: 96px;
    height: 14px;
    border-radius: 6px;
    background: linear-gradient(
      90deg,
      var(--surface-1, #f1f5f9) 0%,
      var(--surface-2, #e2e8f0) 50%,
      var(--surface-1, #f1f5f9) 100%
    );
    background-size: 200% 100%;
    animation: hb-picker-shimmer 1200ms ease-in-out infinite;
  }

  .hb-picker__skeleton--sm { width: 48px; }

  @keyframes hb-picker-shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .hb-picker__skeleton { animation: none; }
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;
